import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/auth-context';
import { usePermissions } from '../../hooks/use-permissions';
import { MENU_COLORS } from '../alliance/bar-menu';
import { CURRENT_APP, CURRENT_APP_FONTS } from '../alliance/current-app';
import { navData, NavDataLink } from './nav-data';

/**
 * The nav links this user can see. One check for the bar and the drawer:
 * `authenticated` links need a signed-in user, and `permissions` links need
 * one of those groups (GlobalAdmin always passes).
 */
export function useNavLinks() {
  // Context
  const { state } = React.useContext(AuthContext);
  // Hooks
  const { checkPermissions } = usePermissions();

  return navData.filter((item) => {
    if (!item.authenticated) {
      return true;
    }
    if (item.permissions) {
      return checkPermissions(item.permissions);
    }
    return state.user !== undefined;
  });
}

/**
 * Whether the current page belongs to a nav link. Links are matched on their
 * first path segment, so /events/FK26 is under Events, every /account page is
 * under Tickets and every /admin page is under Admin. Home only matches /.
 */
const isLinkActive = (params: { item: NavDataLink; pathname: string }) => {
  const section = `/${params.item.to.split('/')[1]}`;
  if (section === '/') {
    return params.pathname === '/';
  }
  return (
    params.pathname === section || params.pathname.startsWith(`${section}/`)
  );
};

const NAV_FONT = (CURRENT_APP_FONTS.nav as React.CSSProperties | null) || {};

/**
 * Desktop top nav in the App Bar: one link per item in Oswald, with an
 * accent underline on the current section.
 */
export function NavTopBar(props: { links: NavDataLink[] }) {
  // Hooks
  const location = useLocation();

  return (
    <Box
      component="nav"
      aria-label={CURRENT_APP.name}
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        alignSelf: 'stretch',
        ml: '28px',
        gap: '2px',
        minWidth: 0,
        // If the links ever outgrow the bar, clip them rather than slide
        // under the user control
        overflow: 'hidden',
      }}
    >
      {props.links.map((item) => {
        const isActive = isLinkActive({ item, pathname: location.pathname });
        return (
          <ButtonBase
            key={item.to}
            component={Link}
            to={item.to}
            focusRipple
            aria-current={isActive ? 'page' : undefined}
            sx={{
              fontFamily: 'inherit',
              ...NAV_FONT,
              height: '100%',
              px: '14px',
              boxSizing: 'border-box',
              borderTop: '3px solid transparent',
              borderBottom: `3px solid ${
                isActive ? CURRENT_APP.accentOnBar : 'transparent'
              }`,
              color: isActive ? '#ffffff' : '#ffffffcc',
              fontSize: 17,
              lineHeight: 1,
              letterSpacing: '.02em',
              whiteSpace: 'nowrap',
              transition: 'color 0.15s',
              '&:hover': { color: '#ffffff' },
              '&.Mui-focusVisible': {
                outline: '2px solid #ffffff',
                outlineOffset: -4,
              },
            }}
          >
            {item.title}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

/** The same links as 48px rows in the nav drawer below md */
export function NavDrawerBody(props: { links: NavDataLink[] }) {
  // Hooks
  const location = useLocation();

  return (
    <Box
      component="nav"
      aria-label={CURRENT_APP.name}
      sx={{
        p: 1,
        // Keep the last links above the CookieYes consent button that is
        // fixed to the bottom-left corner
        pb: '72px',
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        overflowY: 'auto',
        minHeight: 0,
        flex: 1,
      }}
    >
      {props.links.map((item) => {
        const isActive = isLinkActive({ item, pathname: location.pathname });
        return (
          <ButtonBase
            key={item.to}
            component={Link}
            to={item.to}
            aria-current={isActive ? 'page' : undefined}
            sx={{
              fontFamily: 'inherit',
              ...NAV_FONT,
              height: 48,
              px: 2,
              justifyContent: 'flex-start',
              borderRadius: '10px',
              fontSize: 18,
              lineHeight: 1,
              letterSpacing: '.02em',
              color: MENU_COLORS.text,
              // Light app: the active row gets an accent tint (design dBg)
              backgroundColor: isActive
                ? `${CURRENT_APP.accent}1a`
                : 'transparent',
              '&:hover': { backgroundColor: MENU_COLORS.hover },
            }}
          >
            {item.title}
          </ButtonBase>
        );
      })}
    </Box>
  );
}
